// 📱 Service Worker Registration for Mobile Optimization
// Enhanced with offline-first architecture (Netflix/Duolingo style)

import { logger } from './utils/logger';
import { serviceWorkerManager } from './services/serviceWorkerManager';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Auto-reload the page exactly once when a new service worker takes control.
//
// Why: after a deploy, the new SW installs in the background and waits for the
// old SW to be released. Once we tell the new SW to `skipWaiting` (below), it
// activates and claims the page → `controllerchange` fires → we reload so the
// user sees the fresh HTML / CSS / JS without having to delete and reinstall
// the PWA. The `controllerChangeReloaded` guard prevents an infinite reload
// loop (the spec guarantees `controllerchange` fires once per claim, but the
// flag is cheap insurance and also blocks edge cases like manual SW updates
// triggered from DevTools).
let controllerChangeReloaded = false;
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (controllerChangeReloaded) return;
    controllerChangeReloaded = true;
    logger.info('Service Worker: new version active — reloading');
    window.location.reload();
  });
}

function clearServiceWorkerCaches() {
  if (!('caches' in window)) return;
  caches.keys().then((keys) => {
    keys.forEach((key) => {
      caches.delete(key);
    });
  }).catch((error) => {
    logger.warn('Service Worker: Cache clear failed', error);
  });
}

export function register(config?: Config) {
  if (process.env.NODE_ENV !== 'production') {
    unregister();
    clearServiceWorkerCaches();
    return;
  }
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', async () => {
      // Use enhanced service worker manager
      const registered = await serviceWorkerManager.register();
      
      if (registered) {
        const registration = serviceWorkerManager.getRegistration();
        if (registration && config?.onSuccess) {
          config.onSuccess(registration);
        }
        logger.info('Service Worker: Enhanced offline-first architecture active');
      } else {
        // Fallback to original registration
        const swUrl = `${process.env.PUBLIC_URL}/sw.js`;
        if (isLocalhost) {
          checkValidServiceWorker(swUrl, config);
          navigator.serviceWorker.ready.then(() => {
            logger.debug('Service Worker: Ready in localhost');
          });
        } else {
          registerValidSW(swUrl, config);
        }
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      logger.debug('Service Worker: Registered successfully');

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              logger.info('Service Worker: new version installed — activating');
              // Tell the waiting SW to take over immediately. The `controllerchange`
              // listener at the top of this file then reloads the page so the user
              // sees the new HTML/CSS/JS without uninstalling the PWA.
              installingWorker.postMessage({ type: 'SKIP_WAITING' });
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              logger.info('Service Worker: Content cached for offline use');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      logger.error('Service Worker: Registration failed', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      logger.warn('Service Worker: No internet connection found');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        clearServiceWorkerCaches();
      })
      .catch((error) => {
        logger.error('Service Worker: Unregistration failed', error);
      });
  }
}

// Install prompt for PWA
export function registerInstallPrompt() {
  if (process.env.NODE_ENV !== 'production') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    logger.debug('PWA: Install prompt triggered');
    e.preventDefault();

    // TODO: #install-button removed — install prompt now handled by InstallPrompt component in App.tsx
    // Show install button
    // const installButton = document.getElementById('install-button');
    // if (installButton) {
    //   installButton.style.display = 'block';
    //   installButton.addEventListener('click', () => {
    //     if (deferredPrompt) {
    //       deferredPrompt.prompt();
    //       deferredPrompt.userChoice.then((choiceResult: any) => {
    //         if (choiceResult.outcome === 'accepted') {
    //           logger.info('PWA: User accepted install');
    //         } else {
    //           logger.debug('PWA: User dismissed install');
    //         }
    //         deferredPrompt = null;
    //       });
    //     }
    //   });
    // }
  });

  window.addEventListener('appinstalled', () => {
    logger.info('PWA: App installed successfully');
  });
}

// Offline detection
export function registerOfflineDetection() {
  if (process.env.NODE_ENV !== 'production') return;
  const updateOnlineStatus = () => {
    const isOnline = navigator.onLine;
    const statusElement = document.getElementById('offline-status');
    
    if (statusElement) {
      if (isOnline) {
        statusElement.style.display = 'none';
        statusElement.textContent = '';
      } else {
        statusElement.style.display = 'block';
        statusElement.textContent = '📱 You are offline. Some features may be limited.';
      }
    }
  };

  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  updateOnlineStatus();
}

// Haptic feedback for mobile
export function triggerHapticFeedback(type: 'light' | 'medium' | 'heavy' = 'light') {
  if ('vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30]
    };
    
    navigator.vibrate(patterns[type]);
  }
}

// Touch feedback for buttons
export function addTouchFeedback(element: HTMLElement) {
  element.addEventListener('touchstart', () => {
    element.style.transform = 'scale(0.95)';
    triggerHapticFeedback('light');
  });
  
  element.addEventListener('touchend', () => {
    element.style.transform = 'scale(1)';
  });
  
  element.addEventListener('touchcancel', () => {
    element.style.transform = 'scale(1)';
  });
}

// Pull-to-refresh detection
export function registerPullToRefresh(callback: () => void) {
  let startY = 0;
  let currentY = 0;
  let isPulling = false;
  
  document.addEventListener('touchstart', (e) => {
    if (window.scrollY === 0) {
      startY = e.touches[0].clientY;
      isPulling = true;
    }
  });
  
  document.addEventListener('touchmove', (e) => {
    if (isPulling) {
      currentY = e.touches[0].clientY;
      const pullDistance = currentY - startY;
      
      if (pullDistance > 100) {
        // Trigger pull-to-refresh
        callback();
        isPulling = false;
        triggerHapticFeedback('medium');
      }
    }
  });
  
  document.addEventListener('touchend', () => {
    isPulling = false;
  });
}